import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { message } from "antd";
import { createShortUrl, buildShortUrl } from "@/api/shortUrl";

export type CopyState = "idle" | "loading" | "done";

/**
 * Prepares a short link for `targetUrl` (default: the current page) and copies it on demand.
 * Shared by `CopyShortUrlButton` and toolbars that offer "Copy Link" as a plain action.
 */
export function useCopyShortUrl(targetUrl?: string) {
	const [state, setState] = useState<CopyState>("idle");
	const shortLinkRef = useRef<string | null>(null);
	const requestIdRef = useRef(0);
	const location = useLocation();

	useEffect(() => {
		const url = targetUrl ?? location.pathname + location.search;
		const requestId = ++requestIdRef.current;
		shortLinkRef.current = null;

		createShortUrl(url)
			.then((res) => {
				if (requestId !== requestIdRef.current) return;
				shortLinkRef.current = buildShortUrl(res.code, url);
			})
			.catch(console.error);
	}, [targetUrl, location.pathname, location.search]);

	const copy = async () => {
		if (state === "loading") return;

		const link = shortLinkRef.current;
		if (!link) {
			message.error("Short link not ready yet, please try again");
			return;
		}

		try {
			await navigator.clipboard.writeText(link);
			setState("done");
			message.success("Link copied to clipboard!");
			setTimeout(() => setState("idle"), 2000);
		} catch (err) {
			console.error("CopyShortUrlButton clipboard error:", err);
			message.error("Unable to copy link to clipboard");
		}
	};

	const label = state === "loading" ? "Copying..." : state === "done" ? "Copied!" : "Copy Link";
	return { state, label, copy };
}
